import React, {useState, useMemo} from 'react';
import {TextField, styled, Box} from '@mui/material';
import {Body, designSystemToken, Label} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {InputFieldContainer} from '../../shared';

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    'borderRadius': '8px',
    'backgroundColor': designSystemToken('semantic.bg.secondary'),
    'borderColor': designSystemToken('semantic.fg.tertiary'),
    '&:hover': {
      borderColor: designSystemToken('semantic.fg.primary'),
    },
    '&.Mui-focused': {
      borderColor: designSystemToken('semantic.bg.primary'),
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    lineHeight: '18px',
  },
}));

const WordCounterContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '4px',
}));

interface CreativeDescriptionInputProps {
  testID?: string;
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  maxWords?: number;
  showWordCounter?: boolean;
}

function CreativeDescriptionInput({
  testID = 'creative-description-input',
  value,
  onChange,
  label = translate("What's special about your product, service, or campaign?"),
  placeholder = translate(
    'Describe what makes it great, useful, different, etc.'
  ),
  maxWords,
  showWordCounter = false,
}: CreativeDescriptionInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  const wordCount = useMemo(() => {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).filter((word) => word.length > 0).length;
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (maxWords) {
      const trimmed = newValue.trim();
      const newWordCount = trimmed
        ? trimmed.split(/\s+/).filter((word) => word.length > 0).length
        : 0;

      // Prevent exceeding the limit
      if (newWordCount > maxWords) {
        return;
      }

      // If we're at the limit, prevent adding spaces or characters that would create new words
      if (wordCount >= maxWords && newValue.length > value.length) {
        const lastChar = newValue[newValue.length - 1];
        // Block spaces (which could start a new word)
        if (lastChar === ' ') {
          return;
        }
        // Block if word count would increase
        if (newWordCount > wordCount) {
          return;
        }
      }
    }

    onChange(newValue);
  };

  const shouldShowCounter = !!(showWordCounter && maxWords && isFocused);

  const remainingWords = maxWords ? Math.max(0, maxWords - wordCount) : 0;

  return (
    <InputFieldContainer data-testid={testID}>
      <Body size="sm" color={designSystemToken('semantic.fg.primary')}>
        {label}
      </Body>
      <StyledTextField
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        fullWidth
        variant="outlined"
        multiline
        rows={4}
        inputProps={{
          'data-testid': `${testID}-textarea`,
        }}
      />
      {shouldShowCounter && (
        <WordCounterContainer>
          <Label size="xs" color={designSystemToken('semantic.fg.secondary')}>
            {remainingWords} {translate('words left')}
          </Label>
        </WordCounterContainer>
      )}
    </InputFieldContainer>
  );
}

export default CreativeDescriptionInput;
